import { memo, useEffect, useMemo } from 'react';

import { useShortIdContext } from '@air/provider-short-id';

import { useAssetModalRightPanel } from '~/components/AssetModalV2/AssetModalRightPanelContextProvider';
import { PublicAdvancedCollapsibleSection } from '~/components/AssetModalV2/InfoPanel/Advanced/PublicAdvancedCollapsibleSection';
import { CommentsFilterBar } from '~/components/AssetModalV2/InfoPanel/Comments/components/CommentsFilterBar';
import { CommentsVersionsDropdown } from '~/components/AssetModalV2/InfoPanel/Comments/components/CommentsVersionsDropdown';
import { PublicComments } from '~/components/AssetModalV2/InfoPanel/Comments/PublicCommentsPanel';
import { PublicCustomFieldsCollapsibleSection } from '~/components/AssetModalV2/InfoPanel/CustomFields/PublicCustomFieldsCollapsibleSection';
import { PublicCustomMetadataCollapsibleSection } from '~/components/AssetModalV2/InfoPanel/CustomMetadata/PublicCustomMetadataCollapsibleSection';
import { InfoPanel } from '~/components/AssetModalV2/InfoPanel/components/InfoPanel';
import { FileInfoCollapsibleSection } from '~/components/AssetModalV2/InfoPanel/FileInfo/FileInfoCollapsibleSection';
import { usePublicPermissions } from '~/components/PublicPermissionsProvider';
import { useCanViewAllVersionsOnPublicShareLink } from '~/hooks/useCanViewAllVersionsOnPublicShareLink';
import { canSeeAssetCustomFields, canViewAssetDiscussions } from '~/utils/permissions/assetPermissions';

export const PublicAssetInfoPanel = memo(() => {
  const { permissions } = usePublicPermissions();
  const { canViewAllVersions: canViewVersions } = useCanViewAllVersionsOnPublicShareLink();
  const canEdit = false;
  const canViewCustomFields = canSeeAssetCustomFields(permissions);
  const canViewComments = canViewAssetDiscussions(permissions);
  const canDiscuss = canViewAssetDiscussions(permissions);
  const { presentationSettings } = useShortIdContext();
  const hideInfoTab = presentationSettings?.hideInfoTab === true;
  const { closeRightPanel } = useAssetModalRightPanel();

  const contentHeader = useMemo(
    () =>
      canDiscuss && canViewVersions ? (
        <CommentsFilterBar>
          <CommentsVersionsDropdown />
        </CommentsFilterBar>
      ) : undefined,
    [canDiscuss, canViewVersions],
  );

  useEffect(() => {
    if (hideInfoTab && !canViewComments) {
      closeRightPanel();
    }
  }, [canViewComments, closeRightPanel, hideInfoTab]);

  return (
    <InfoPanel
      canEdit={canEdit}
      canViewInfo={!hideInfoTab}
      canViewVersions={canViewVersions}
      canViewComments={canViewComments}
      infoView={
        <div className="flex flex-col gap-2 pb-4">
          {canViewCustomFields && <PublicCustomFieldsCollapsibleSection />}
          <PublicCustomMetadataCollapsibleSection />
          <FileInfoCollapsibleSection />
          <PublicAdvancedCollapsibleSection />
        </div>
      }
      commentsView={
        <div className="flex h-full flex-col">
          {contentHeader}
          <div className="min-h-0 flex-1">
            <PublicComments />
          </div>
        </div>
      }
    />
  );
});

PublicAssetInfoPanel.displayName = 'PublicAssetInfoPanel';
